import { BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export const CHART_COLORS = ["#2563EB", "#0EA5E9", "#10B981", "#F59E0B", "#7C3AED", "#EF4444", "#EC4899", "#14B8A6"];
export const tooltipStyle = { backgroundColor: "#fff", border: "1px solid #E5E7EB", borderRadius: 8, color: "#0F172A", fontSize: 12, padding: "8px 12px", boxShadow: "0 4px 12px rgba(15,23,42,0.08)" };

export function formatNum(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + "K";
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toFixed(2);
}

export default function WidgetRenderer({ widget, height = 260 }) {
  if (!widget) return null;
  const r = widget.rendered || widget;
  const title = widget.config?.title || widget.title || "";
  const filter = widget.config?.filter || widget.filter;

  if (r?.error) {
    return (
      <div className="card">
        {title && <div className="label-caps mb-3">{title}</div>}
        <div className="text-xs text-[var(--danger)]">{r.error}</div>
      </div>
    );
  }

  const type = r?.type || "chart";

  if (type === "kpi") {
    return (
      <div className="card h-full">
        <div className="label-caps mb-3 truncate">{title || r.label}</div>
        <div className="kpi-value" data-testid="widget-kpi-value">{formatNum(r.value)}</div>
        {r.agg && r.column && (
          <div className="text-xs text-slate-500 mt-3 mono">{r.agg} · {r.column}</div>
        )}
        {filter && <FilterChip filter={filter} className="mt-3" />}
      </div>
    );
  }

  if (type === "table") {
    return (
      <div className="card-flush">
        <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <div className="label-caps">{title || "Table"}</div>
          <span className="text-[10px] text-slate-500 mono">{r.total_rows} rows</span>
        </div>
        <div className="overflow-auto scroll-thin max-h-[360px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 border-b border-[var(--border)]">
              <tr>
                {(r.columns || []).map((c) => <th key={c} className="text-left px-4 py-2.5 label-caps font-medium">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {(r.rows || []).map((row, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50/50">
                  {(r.columns || []).map((c) => (
                    <td key={c} className={`px-4 py-2 ${row[c] == null ? "text-slate-400" : "text-slate-800"}`}>
                      {row[c] == null ? "—" : String(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="label-caps truncate">{title}</div>
        {filter && <FilterChip filter={filter} />}
      </div>
      <ChartInner spec={r} height={height} />
    </div>
  );
}

function ChartInner({ spec, height }) {
  if (!spec?.data?.length) return <div className="text-xs text-slate-400 py-12 text-center">No data.</div>;
  const { chart_type, x, y, data } = spec;
  if (chart_type === "pie") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey={y} nameKey={x} innerRadius={50} outerRadius={90} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11, color: "#475569" }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  const Wrapper = chart_type === "line" ? LineChart : chart_type === "area" ? AreaChart : BarChart;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <Wrapper data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
        <XAxis dataKey={x} stroke="#94A3B8" style={{ fontSize: 11 }} />
        <YAxis stroke="#94A3B8" style={{ fontSize: 11 }} />
        <Tooltip contentStyle={tooltipStyle} />
        {chart_type === "line" && <Line type="monotone" dataKey={y} stroke="#2563EB" strokeWidth={2} dot={{ r: 3, fill: "#2563EB" }} />}
        {chart_type === "area" && (
          <>
            <defs>
              <linearGradient id="wf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563EB" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey={y} stroke="#2563EB" strokeWidth={2} fill="url(#wf)" />
          </>
        )}
        {chart_type === "bar" && <Bar dataKey={y} fill="#2563EB" radius={[4, 4, 0, 0]} />}
      </Wrapper>
    </ResponsiveContainer>
  );
}

function FilterChip({ filter, className = "" }) {
  if (!filter?.column) return null;
  return <span className={`chip ${className}`}>filter · {filter.column} {filter.op || "="} {String(filter.value)}</span>;
}
